import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AlertService } from '@app/_services';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { first } from 'rxjs/operators';
import { QualityTestDefinitionsService } from '../quality-test-definitions.service';
import { QualityTestDefinition } from '../quality-test-definition';
import { QualityTestDefinitionFormComponent } from '../quality-test-definition-form/quality-test-definition-form.component';

@Component({
  selector: 'app-quality-test-definitions-list',
  templateUrl: './quality-test-definitions-list.component.html',
  styleUrls: ['./quality-test-definitions-list.component.css'],
  providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
  styles: [`
    :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
      position: -webkit-sticky;
      position: sticky;
      top: 0px;
    }
    @media screen and (max-width: 64em) {
      :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
        top: 99px;
      }
    }
  `],
  standalone: false
})
export class QualityTestDefinitionsListComponent implements OnInit {

  definitions: QualityTestDefinition[] = [];
  selectedDefinition: QualityTestDefinition;

  recordTotal = 0;
  loading = true;
  lazyLoadEvent: LazyLoadEvent;
  multiSortMeta: any[] = [];

  formLoading = false;

  columns: any[] = [
    { field: 'testName', header: 'Test Name' },
    { field: 'testCategory', header: 'Category' },
    { field: 'ruleType', header: 'Rule Type' },
    { field: 'ruleField', header: 'Rule Field' },
    { field: 'severity', header: 'Severity' },
    { field: 'scoreWeight', header: 'Weight' },
    { field: 'isActive', header: 'Active' },
  ];

  constructor(
    private service: QualityTestDefinitionsService,
    private alertService: AlertService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    try {
      this.multiSortMeta = [];
      this.multiSortMeta.push({ field: 'sortOrder', order: 1 });
    } catch (error) { }
  }

  loadDefinitions(event: LazyLoadEvent) {
    try {
      this.loading = true;
      this.lazyLoadEvent = event;

      if (Object.keys(event.filters).length === 0 && !event.multiSortMeta) {
        event.multiSortMeta = [];
        event.multiSortMeta.push({ field: 'sortOrder', order: 1 });
      }

      const pageNumber = Math.round(event.first / event.rows);
      this.service.findAll(
        pageNumber, event.rows,
        event.sortField,
        event.sortOrder,
        event.multiSortMeta,
        event.globalFilter,
        event.filters
      ).pipe(first()).subscribe(
        (res: any) => {
          this.definitions = res.data?.qualityTestDefinitions || [];
          this.recordTotal = res.page ? res.page.recordTotal : (this.definitions as any[]).length;
        },
        (err: any) => {
          this.alertService.error(err);
        },
        () => { this.loading = false; }
      );
    } catch (error) { }
  }

  onRowSelect(event: any) {
    this.openForm(event.data);
  }

  openNew() {
    this.openForm(null);
  }

  openForm(def: QualityTestDefinition | null) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.width = '800px';
    dialogConfig.data = def ? { ...def } : null;

    const dialogRef = this.dialog.open(QualityTestDefinitionFormComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadDefinitions(this.lazyLoadEvent);
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Definition saved successfully.', life: 3000 });
      }
    });
  }

  onRowDelete(def: QualityTestDefinition) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete "' + def.testName + '"?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.formLoading = true;
        this.service.delete(def.testDefinitionId).pipe(first()).subscribe(
          (res: any) => {
            this.definitions = this.definitions.filter(d => d.testDefinitionId !== def.testDefinitionId);
            this.recordTotal = this.definitions.length;
            this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Definition deleted.', life: 3000 });
          },
          (err: any) => {
            this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: JSON.stringify(err), life: 3000 });
          },
          () => { this.formLoading = false; }
        );
      },
      reject: () => {
        this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
      }
    });
  }

  getSeverityTag(severity: string): string {
    if (severity === 'FAIL') return 'danger';
    if (severity === 'WARN') return 'warning';
    return 'info';
  }
}
